import { Component, computed, DestroyRef, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouteFiltersApi } from '../../../../../pages/admin-dashboard/admin-route-management/routes.model';
import { LookupService } from '../../../../../services/pharmacy/lookup.service';
import { RoutesService } from '../../../../../services/route/route-service';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterSelectComponent } from '../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterMultiSelectComponent } from '../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap } from 'rxjs';

@Component({
  selector: 'app-route-filters',
  imports: [
    FormsModule,
    FilterSortingComponent,
    FilterInput,
    FilterSelectComponent,
    FilterMultiSelectComponent,
    FilterDateComponent,
  ],
  templateUrl: './route-filters.html',
  styleUrl: './route-filters.scss',
  standalone: true,
})
export class RouteFilters implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private routesService = inject(RoutesService);
  private destroyRef = inject(DestroyRef);

  filters = input.required<RouteFiltersApi>();
  applyFilters = output<RouteFiltersApi>();
  toggleSortOrder = output();
  resetFilters = output();

  routeName = signal('');
  isActive = signal<string | null>(null);

  governorates = signal<LookupVTO[]>([]);
  selectedGovernorateIds = signal<string[]>([]);
  showGovernorateDropdown = signal(false);
  loadingGovernorates = signal(false);

  cities = signal<LookupVTO[]>([]);
  selectedCityId = signal<number | null>(null);
  loadingCities = signal(false);

  regions = signal<LookupVTO[]>([]);
  selectedRegionId = signal<number | null>(null);
  loadingRegions = signal(false);

  intervals = signal<{ id: number; titleEn: string; titleAr: string; profileImageFid: string }[]>([]);
  selectedIntervalIds = signal<string[]>([]);
  showIntervalDropdown = signal(false);
  loadingIntervals = signal(false);

  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);

  createdDateInvalid = signal(false);
  lastModifiedDateInvalid = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));

  private governorateChange$ = new Subject<number[]>();
  private cityChange$ = new Subject<number[]>();

  ngOnInit(): void {
    this.loadGovernorates();
    this.loadIntervals();

    this.governorateChange$
      .pipe(
        switchMap((ids) => {
          this.loadingCities.set(true);
          return this.lookupService.getCities(ids);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.cities.set(res.list);
          this.loadingCities.set(false);
        },
        error: () => this.loadingCities.set(false),
      });

    this.cityChange$
      .pipe(
        switchMap((ids) => {
          this.loadingRegions.set(true);
          return this.lookupService.getRegions(ids);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.regions.set(res.list);
          this.loadingRegions.set(false);
        },
        error: () => this.loadingRegions.set(false),
      });
  }

  loadGovernorates() {
    this.loadingGovernorates.set(true);
    const subscription = this.lookupService.getGovernorates?.().subscribe({
      next: (res) => {
        this.governorates.set(res.list);
        this.loadingGovernorates.set(false);
      },
      error: () => this.loadingGovernorates.set(false),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  loadIntervals() {
    this.loadingIntervals.set(true);
    const subscription = this.routesService.getRouteIntervalCounts().subscribe({
      next: (res) => {
        this.intervals.set(res.list);
        this.loadingIntervals.set(false);
      },
      error: () => this.loadingIntervals.set(false),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  governorateOptions = computed(() =>
    this.governorates().map((g) => ({ id: String(g.id), titleAr: g.titleAr, titleEn: g.titleEn })),
  );

  toggleGovernorateDropdown() {
    this.showGovernorateDropdown.set(!this.showGovernorateDropdown());
    this.showIntervalDropdown.set(false);
  }

  onGovernorateChange(ids: string[]) {
    this.selectedGovernorateIds.set(ids);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);

    if (ids.length > 0) {
      this.governorateChange$.next(ids.map(Number));
    }
  }

  cityOptions = computed(() => this.cities().map((c) => ({ id: c.id, titleAr: c.titleAr, titleEn: c.titleEn })));

  onCityChange(cityId: any) {
    this.selectedCityId.set(cityId ?? null);
    this.selectedRegionId.set(null);
    this.regions.set([]);

    if (cityId) {
      this.cityChange$.next([Number(cityId)]);
    }
  }

  regionOptions = computed(() => this.regions().map((r) => ({ id: r.id, titleAr: r.titleAr, titleEn: r.titleEn })));

  intervalOptions = computed(() =>
    this.intervals().map((i) => ({ id: String(i.id), titleAr: i.titleAr, titleEn: i.titleEn })),
  );

  toggleIntervalDropdown() {
    this.showIntervalDropdown.set(!this.showIntervalDropdown());
    this.showGovernorateDropdown.set(false);
  }

  activeOptions = computed(() => [
    { id: 'true', titleAr: 'مفعل', titleEn: 'Active' },
    { id: 'false', titleAr: 'غير مفعل', titleEn: 'Inactive' },
  ]);

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified', labelAr: 'آخر تعديل' },
  ]);

  closeAllDropdowns() {
    this.showGovernorateDropdown.set(false);
    this.showIntervalDropdown.set(false);
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }

  onResetFilters() {
    this.routeName.set('');
    this.isActive.set(null);
    this.selectedGovernorateIds.set([]);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.selectedIntervalIds.set([]);
    this.cities.set([]);
    this.regions.set([]);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.closeAllDropdowns();
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);
    if (createdInvalid || lastModInvalid) return;

    const isActiveValue = this.isActive();
    const isActiveParsed =
      isActiveValue === null
        ? undefined
        : String(isActiveValue) === 'true'
          ? true
          : String(isActiveValue) === 'false'
            ? false
            : undefined;

    this.applyFilters.emit({
      routeName: this.routeName() || undefined,
      isActive: isActiveParsed,
      governorateIds: this.selectedGovernorateIds().length ? this.selectedGovernorateIds().map(Number) : undefined,
      cityIds: this.selectedCityId() ? [this.selectedCityId()!] : undefined,
      regionIds: this.selectedRegionId() ? [this.selectedRegionId()!] : undefined,
      intervalCountIds: this.selectedIntervalIds().length ? this.selectedIntervalIds().map(Number) : undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
    });
  }
}
